import Error404View from '@auth/views/Error404View'
import {
  ContentWrapper,
  CustomPreloader,
  CustomToaster,
  ErrorFallback,
  Header,
  MaintenanceModal,
  ModalDialog
} from '@components'
import Otp from '@components/Otp'
import settings from '@config/settings'
import { ThemeProvider } from '@config/ThemeContext'
import theme from '@config/theme'
import { COOKIE_NAME } from '@constants'
import { CookieConsent } from '@cookieConsent'
import cn from '@integrations/classnames'
import { CookiesProvider, useCookies } from '@integrations/cookies'
import { ErrorBoundary } from '@integrations/errors'
import { type ReactNode, Suspense, useCallback, useEffect, useMemo, useState } from '@integrations/react'
import { Redirect, RouteTable, useLocation } from '@integrations/routing'
import { withTranslation } from '@integrations/translation'
import { nonProtectedRoutes, protectedRoutes } from '@routes'
import SupportFab from '@support/components/SupportFab/SupportFab'
import { reloadOnStaleChunk, uiSize } from '@utils'
import {
  LoginStatuses,
  paths,
  selectCurrentUserRole,
  selectCurrentUserStatus,
  selectMerchantEntityType,
  useAppInitializerQry,
  useAppSelector,
  useAuthQry,
  useLazyErrorLoggingQuery
} from 'mmfintech-backend-api'
import type { RouteDefinition } from 'mmfintech-backend-api/types'
import { GlobalContextProvider, isMobileDevice, isValidObject, OtpContext } from 'mmfintech-commons'
import { MMGlobalSettingsProvider } from 'mmfintech-portal-commons'
import type { MMGlobalSettings } from 'mmfintech-portal-commons/types'

function getLoginRedirect(status: string) {
  switch (status) {
    case LoginStatuses.NOT_VERIFIED:
      return paths.checkEmail()
    case LoginStatuses.FORGOT_PASSWORD:
      return paths.changePasswordEmail()
    case LoginStatuses.RESET_PASSWORD:
      return paths.handlePasswordResetInvited()
    case LoginStatuses.PASSWORD_RESET:
      return paths.passwordReset()
    default:
      return paths.login()
  }
}

function PublicRouteElement({ definition }: { definition: RouteDefinition }) {
  if (definition.redirect) return <Redirect to={definition.redirect} />
  if (!definition.component) return null

  const Component = definition.component
  return <Component {...definition.params} />
}

function PrivateRouteElement({ definition }: { definition: RouteDefinition }) {
  const status = useAppSelector(selectCurrentUserStatus)
  const userRole = useAppSelector(selectCurrentUserRole)
  const entityType = useAppSelector(selectMerchantEntityType)
  const location = useLocation()

  if (status !== LoginStatuses.LOGGED_IN) {
    return <Redirect to={{ pathname: getLoginRedirect(status), state: { from: location } }} />
  }

  const roleAllowed =
    !definition.userRole ||
    (Array.isArray(definition.userRole)
      ? definition.userRole.includes(userRole ?? '')
      : definition.userRole === userRole)
  const entityAllowed = !definition.entityType || definition.entityType === entityType

  return roleAllowed && entityAllowed ? <PublicRouteElement definition={definition} /> : null
}

function AppInner() {
  useAppInitializerQry(settings.languages)
  const [cookies, setCookie] = useCookies([COOKIE_NAME.CONSENT])

  const { isAuthFetching } = useAuthQry()

  const [cookieConsent, setCookieConsent] = useState(null)

  const userStatus = useAppSelector(selectCurrentUserStatus)
  const queryChallenge = useAppSelector((state) => state.challenge)

  const isLoggedIn = useMemo(() => userStatus === LoginStatuses.LOGGED_IN, [userStatus])

  useEffect(() => {
    setCookieConsent(cookies[COOKIE_NAME.CONSENT])
  }, [cookies])

  // biome-ignore lint/correctness/useExhaustiveDependencies: run only when queryChallenge changes, not callbacks
  useEffect(() => {
    if (queryChallenge && 'challengeId' in queryChallenge) {
      modalShow({
        options: {
          size: 'medium',
          transparent: true,
          closeOnClickOutside: false,
          closeOnEscape: false
        },
        content: <Otp />
      })
    } else {
      modalHide()
    }
  }, [queryChallenge])

  const [modalContent, setModalContent] = useState<ReactNode>(null)
  const [modalOptions, setModalOptions] = useState(null)
  const [modalVisible, setModalVisible] = useState<boolean>(false)

  const [otpOnSuccess, setOtpOnSuccess] = useState(null)
  const [otpOnError, setOtpOnError] = useState(null)

  const modalHide = useCallback(() => {
    setModalVisible(false)
    setModalContent(null)
    setModalOptions(null)
  }, [])

  const modalShow = useCallback(({ options, content }) => {
    setModalContent(content)
    setModalOptions(options)
    setModalVisible(true)
  }, [])

  const globalContext = useMemo(() => ({ modalHide, modalShow }), [modalHide, modalShow])

  const otpContext = useMemo(
    () => ({ otpOnSuccess, setOtpOnSuccess, otpOnError, setOtpOnError }),
    [otpOnError, otpOnSuccess]
  )

  const mmGlobalSettings: Partial<MMGlobalSettings> = {
    buttonLoadingStyle: 'ThreeDots',
    buttonLoadingColor: 'var(--banky-white)',
    inputHeight: uiSize(44)
  }

  return isAuthFetching ? (
    <CustomPreloader />
  ) : (
    <MMGlobalSettingsProvider settings={mmGlobalSettings}>
      <ThemeProvider theme={theme}>
        <MaintenanceModal />
        <GlobalContextProvider context={globalContext}>
          <OtpContext.Provider value={otpContext}>
            <ContentWrapper isLoggedIn={isLoggedIn}>
              {isLoggedIn && <Header />}

              <RouteTable
                routes={[
                  ...nonProtectedRoutes.map((definition) => ({
                    path: definition.path,
                    element: <PublicRouteElement definition={definition} />
                  })),
                  ...protectedRoutes.map((definition) => ({
                    path: definition.path,
                    element: <PrivateRouteElement definition={definition} />
                  })),
                  { path: '*', element: <Error404View /> }
                ]}
              />

              {isValidObject(cookieConsent) || isMobileDevice() ? null : <CookieConsent setCookie={setCookie} />}

              <ModalDialog content={modalContent} options={modalOptions} visible={modalVisible} onClose={modalHide} />
            </ContentWrapper>

            <CustomToaster />
            <SupportFab />
          </OtpContext.Provider>
        </GlobalContextProvider>
      </ThemeProvider>
    </MMGlobalSettingsProvider>
  )
}

const ThisApp = withTranslation()(AppInner)

function App() {
  const [sendError] = useLazyErrorLoggingQuery()

  return (
    <CookiesProvider>
      <Suspense
        fallback={
          <div className={cn('global-centered')}>
            <CustomPreloader />
          </div>
        }
      >
        <ErrorBoundary
          FallbackComponent={ErrorFallback}
          onError={(err, { componentStack }) => {
            if (reloadOnStaleChunk(err)) {
              return
            }
            return sendError({ level: 'ERROR', componentStack, message: err.toString() })
          }}
          onReset={() => {
            window.location.replace('/')
          }}
        >
          <ThisApp />
        </ErrorBoundary>
      </Suspense>
    </CookiesProvider>
  )
}

export default App
